import { DirectionalHint } from "@fluentui/react";
import React, { useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";

import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure, ValidationResult } from "../../utils/validation/ValidationResult";
import { DropdownOption } from "../inputs";
import { Checkbox } from "../inputs/checkbox/Checkbox";
import { Label } from "../inputs/label/Label";
import { Text } from "../items/Text";
import { Stack } from "../layouts/stack/Stack";
import { TooltipHost } from "../surfaces/tooltip/TooltipHost";
import { Theme } from "../theme/Theme";
import { ListItemFailures } from "./common/FormSelectList";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";
import { useFormWatch } from "./useFormWatch";

export interface FormSingleSelectListProps {
    items: DropdownOption[];
    control: Control;
    name: string;
    errors: FieldErrors;
    label: string;
    rules?: FormValidationRules<string>;
    testId?: string;
    minHeight?: number;
    maxHeight?: number;
    disabled?: boolean | string;
    onItemValidating?: (itemId: string) => ValidationResult;
    isItemDisabled?: (itemId: string, selectedItem?: string) => boolean;
    onChange?(selectedItem?: string): void;
    renderErrorMsg?(): JSX.Element;
    dependsOn?: string[];
    actionButtons?: JSX.Element[];
    renderAdditionalInfo?: (itemId: string, isItemInactive?: boolean) => JSX.Element;
}

export const FormSingleSelectList = (props: FormSingleSelectListProps): JSX.Element => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);
    const { control, name, dependsOn, testId, onChange, actionButtons } = props;

    const clearWarnings = () => {
        setWarnings([]);
    };

    const handleOnChange = () => {
        if (onChange) {
            onChange(undefined);
            clearWarnings();
        }
    };

    if (dependsOn) {
        useFormWatch({ name, control, dependsOn, onClear: handleOnChange });
    }

    const render = (prop: { value?: string; onChange(value?: string): void }) => {
        const selectedItem = prop.value;

        const selectItem = (itemId: string, checked?: boolean) => {
            clearWarnings();

            const newValue = checked ? itemId : undefined;

            prop.onChange(newValue);
            if (props.onChange) props.onChange(newValue);
        };

        const isSelected = (item: DropdownOption) => {
            return selectedItem === item.key;
        };

        const renderListItem = (item: DropdownOption) => {
            if (!item) return null;

            const handleItemChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
                selectItem(item.key, checked);
            };

            let validationResult: ValidationResult | undefined;
            if (props.onItemValidating) {
                validationResult = props.onItemValidating(item.key);
            }

            let isDisabled = false;
            if (props.isItemDisabled) {
                isDisabled = props.isItemDisabled(item.key, selectedItem);
            }

            return (
                <Stack
                    horizontal={true}
                    tokens={{ childrenGap: 4 }}
                    styles={{ root: { padding: 6 } }}
                    data-testId={testId}
                    key={item.key}
                >
                    <Checkbox
                        label={undefined}
                        checked={isSelected(item)}
                        disabled={isDisabled}
                        onChange={handleItemChange}
                        styles={{ root: { padding: 0 }, label: { width: 20 } }}
                        testId={`checkbox-${item.text}`}
                    />
                    <Text disabled={isDisabled}>{item.text}</Text>
                    <ListItemFailures isDisabled={isDisabled} result={validationResult} />
                    {props.renderAdditionalInfo && props.renderAdditionalInfo(item.key, isDisabled)}
                </Stack>
            );
        };

        const hasAnyItems = props.items.length === 0;

        return (
            <Stack>
                <Stack horizontal={true} styles={{ root: { width: "100%", display: "flex" } }}>
                    <TooltipHost directionalHint={DirectionalHint.rightCenter} content={""} hidden={!hasAnyItems}>
                        <Label disabled={hasAnyItems}>{getFormLabelText(props.label, !!props.rules?.required)}</Label>
                    </TooltipHost>
                    {actionButtons}
                </Stack>
                <Stack
                    styles={{
                        root: {
                            minHeight: props.minHeight,
                            maxHeight: props.maxHeight,
                            width: "100%",
                            border: props.disabled
                                ? `solid 1px ${Theme.palette.neutralTertiary}`
                                : props.errors[props.name]
                                ? "solid 2px red"
                                : "solid 1px",
                            WebkitBorderRadius: "2px",
                            overflow: "auto"
                        }
                    }}
                >
                    {props.items.map(renderListItem)}
                </Stack>
            </Stack>
        );
    };

    return (
        <Stack>
            <Controller
                name={props.name}
                control={props.control}
                rules={{
                    ...props.rules,
                    validate: getValidate<string, FormValidationRules<string>>(
                        props.rules,
                        props.control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            {props.renderErrorMsg ? (
                props.renderErrorMsg()
            ) : (
                <FormErrorMessage name={props.name} errors={props.errors} testId={`${props.name}-error`} />
            )}
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} />
        </Stack>
    );
};
